import React from 'react'
import {
  closestCenter,
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

type ComponentType = {
  onDragStart?: (event: DragStartEvent) => void
  onDragEnd: (event: DragEndEvent) => void
  list: Array<any>
  children: any
  disabled?: boolean
  // Virtualization support - renders overlay for dragged item
  renderDragOverlay?: (activeId: string | number | null) => React.ReactNode
}

export const DraggableContainer: React.FC<ComponentType> = ({
  onDragStart,
  onDragEnd,
  list,
  children,
  disabled,
  renderDragOverlay,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const [activeId, setActiveId] = React.useState<string | number | null>(null)

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id)
    onDragStart?.(event)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null)
    onDragEnd(event)
  }

  if (disabled) {
    return children
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext items={list} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>

      {/* DragOverlay for virtualized lists */}
      {renderDragOverlay && (
        <DragOverlay>
          {activeId ? renderDragOverlay(activeId) : null}
        </DragOverlay>
      )}
    </DndContext>
  )
}
